// src/service/user/LoginUserContext.tsx
/**
 * ログインユーザーの情報を管理するコンテキスト
 */
import React, { createContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { onAuthStateChanged, signOut, User as FirebaseUser } from 'firebase/auth';
import { auth } from '../../firebase';
import { fetchUserProfile } from '../../hooks/useUserProfile';
import { logger } from '../../utils/logger';
import { Profile } from '../../types'; // Profile型をインポート



// UserContext の値の型定義
interface UserContextType {
  user: FirebaseUser | null;
  userData: Profile | null;
  uid: string | null;
  loading: boolean;
  setUser: React.Dispatch<React.SetStateAction<FirebaseUser | null>>;
  setUserData: React.Dispatch<React.SetStateAction<Profile | null>>;
  logout: () => Promise<void>;
}

export const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<Profile | null>(null);
  const [uid, setUid] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
      setCurrentUser(user);
      setUid(user?.uid || null);

      if (user) {
        try {
          // profileService.ts の fetchUserProfile を使用してユーザーデータを取得
          const userProfile = await fetchUserProfile(user.uid);
          setUserData(userProfile);
        } catch (error) {
          logger.error('Error fetching userData:', error);
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    setCurrentUser(null);
    setUserData(null);
    setUid(null);
  }, []);

  return (
    <UserContext.Provider value={{
      user: currentUser,
      userData,
      uid,
      loading,
      setUser: setCurrentUser,
      setUserData,
      logout,
    }}>
      {children}
    </UserContext.Provider>
  );
};